import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarDays, Clock, Plus, Trash2 } from "lucide-react";
import api, { apiError, notifyChange } from "../lib/api";
import { toast } from "sonner";

const localISO = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDaysISO = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return localISO(d);
};

export default function MedicalAvailabilityPanel({ doctor, canEdit = false }) {
  const defaultDuration = Number(doctor.default_slot_duration_minutes || 20);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    date: localISO(),
    start: "09:00",
    end: "13:00",
    slot_duration_minutes: defaultDuration,
  });

  useEffect(() => {
    setForm((f) => ({ ...f, slot_duration_minutes: defaultDuration }));
  }, [defaultDuration]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/medical-availabilities", {
        params: {
          date_from: localISO(),
          date_to: addDaysISO(180),
          physio_id: doctor.id,
          include_inactive: false,
        },
      });
      setItems(data);
    } catch (e) {
      toast.error(apiError(e, "Errore nel caricamento delle disponibilità"));
    } finally {
      setLoading(false);
    }
  }, [doctor.id]);

  useEffect(() => { load(); }, [load]);

  const slotCount = useMemo(() => {
    const toMin = (t) => {
      const [h, m] = t.split(":").map(Number);
      return h * 60 + m;
    };
    const length = toMin(form.end) - toMin(form.start);
    return length > 0 ? Math.floor(length / Number(form.slot_duration_minutes || 1)) : 0;
  }, [form]);

  const save = async () => {
    if (!form.date) { toast.error("Seleziona una data"); return; }
    if (form.end <= form.start) { toast.error("L'orario finale deve essere successivo all'inizio"); return; }
    if (slotCount < 1) { toast.error("La fascia deve contenere almeno uno slot completo"); return; }
    setSaving(true);
    try {
      await api.post("/medical-availabilities", {
        physio_id: doctor.id,
        date: form.date,
        start: form.start,
        end: form.end,
        slot_duration_minutes: Number(form.slot_duration_minutes),
        specialization: doctor.specialization || "",
        active: true,
      });
      toast.success("Disponibilità medica creata");
      notifyChange();
      await load();
    } catch (e) {
      toast.error(apiError(e, "Impossibile creare la disponibilità"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Disattivare la disponibilità del ${item.date} ${item.start}–${item.end}?`)) return;
    try {
      await api.delete(`/medical-availabilities/${item.id}`, { params: { version: item.version } });
      toast.success("Disponibilità disattivata");
      notifyChange();
      await load();
    } catch (e) {
      toast.error(apiError(e, "Impossibile modificare la disponibilità"));
    }
  };

  return (
    <div className="space-y-3" data-testid={`medical-availability-panel-${doctor.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-400">Disponibilità mediche</p>
          <p className="text-xs text-slate-500">Slot predefinito: {defaultDuration} min</p>
        </div>
        {loading && <span className="text-xs text-slate-400">Caricamento...</span>}
      </div>

      <div className="space-y-1.5">
        {items.map((item) => (
          <div key={item.id} className="flex items-center gap-2 rounded-xl border border-slate-100 bg-slate-50/70 px-3 py-2 text-xs">
            <CalendarDays size={13} className="text-blue-500 shrink-0" />
            <span className="font-medium text-slate-700">{new Date(item.date + "T12:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" })}</span>
            <span className="font-mono text-slate-500">{item.start}–{item.end}</span>
            <span className="rounded-full bg-white border border-slate-200 px-2 py-0.5 text-slate-500">{item.slot_duration_minutes} min</span>
            {canEdit && (
              <button onClick={() => remove(item)} className="ml-auto text-slate-300 hover:text-red-500" title="Disattiva disponibilità" data-testid={`medical-availability-delete-${item.id}`}>
                <Trash2 size={13} />
              </button>
            )}
          </div>
        ))}
        {!loading && items.length === 0 && <p className="text-xs text-slate-400">Nessuna disponibilità programmata nei prossimi 180 giorni.</p>}
      </div>

      {canEdit && (
        <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-3 space-y-2" data-testid={`medical-availability-form-${doctor.id}`}>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[10px] uppercase tracking-wide text-slate-400 col-span-2">Nuova giornata</label>
            <input type="date" min={localISO()} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-lg border border-slate-200 bg-white px-2 py-2 text-xs" data-testid="medical-availability-date" />
            <select value={String(form.slot_duration_minutes)} onChange={(e) => setForm({ ...form, slot_duration_minutes: Number(e.target.value) })} className="rounded-lg border border-slate-200 bg-white px-2 py-2 text-xs" data-testid="medical-availability-duration">
              {[15, 20, 30, 40, 45, 60].map((d) => <option key={d} value={d}>Slot {d} min</option>)}
            </select>
            <div className="flex items-center gap-1 col-span-2">
              <Clock size={13} className="text-slate-400" />
              <input type="time" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className="w-full rounded-lg border border-slate-200 bg-white px-2 py-2 text-xs" data-testid="medical-availability-start" />
              <span className="text-slate-400">–</span>
              <input type="time" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} className="w-full rounded-lg border border-slate-200 bg-white px-2 py-2 text-xs" data-testid="medical-availability-end" />
            </div>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-slate-500">{slotCount} slot completi</span>
            <button onClick={save} disabled={saving} className="flex items-center gap-1 rounded-full bg-blue-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50" data-testid="medical-availability-save">
              <Plus size={13} /> {saving ? "Salvataggio..." : "Aggiungi"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
